import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    fetchPriceListPayload,
    PriceListParams,
    ProfitabilityBreakdownRow,
    ProfitabilityCaseInfo,
    ProfitabilityHeaderColumn,
    ProfitabilityOffer,
    ProfitabilityViewMode,
    CockpitColumnDef,
    CockpitRow,
} from "../types";
import { toNumericOrZero } from "../numericValue";
import DATAMART_SERVICES from "./DatamartServices";
import CONFIGURATOR_SERVICES from "./ConfiguratorService";
import { PopupUtils } from "Utils/PopupUtils";
import { getAnalyseProfitabilityBlockReason } from "Constants/ProfitabilityRoles";
import {
    parseCockpitPayload,
    buildDiscountCockpitPayload,
    cloneCockpitRows,
    normaliseCockpitRowData,
} from "../Analyse/cockpitUtils";
import {
    resolveAnalysisColumnPrefs,
} from "./AnalysisColumnPrefsService";
import {
    PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
    mergeApiColumnsWithDefs,
    normalizePrefsVisibility,
    toColumnPrefs,
} from "../Analyse/columnPrefsUtils";

/** Maps FE view mode → GET path segment (View is POST and has no path). */
export const PROFITABILITY_MODE_PATH: Record<
    Exclude<ProfitabilityViewMode, "view">,
    "l2" | "l3" | "l2l3" | "l3l2"
> = {
    l2: "l2",
    l3: "l3",
    l2_l3: "l2l3",
    l3_l2: "l3l2",
};

export const isNestedProfitabilityMode = (mode: ProfitabilityViewMode) =>
    mode === "l2_l3" || mode === "l3_l2";

// Item collections in the profitabilityUI payload that carry editable values.
const PROFITABILITY_ITEM_COLLECTIONS = [
    'saleItems',
    'addOns',
    'additionalItems',
    'mpwItems',
    'sisServices',
] as const;

const PAYLOAD_CATEGORY_FIELD = 'profitability_payload_category';

type PayloadItemCollection = typeof PROFITABILITY_ITEM_COLLECTIONS[number];

const isPayloadItemCollection = (key: string): key is PayloadItemCollection =>
    (PROFITABILITY_ITEM_COLLECTIONS as readonly string[]).includes(key);

/** Resolves the payload bucket key for a line item (DB field or collection fallback). */
const resolvePayloadBucket = (
    item: Record<string, any> = {},
    fallbackBucket: string = 'saleItems',
): PayloadItemCollection => {
    const category = item[PAYLOAD_CATEGORY_FIELD];
    if (typeof category === 'string' && isPayloadItemCollection(category)) {
        return category;
    }
    if (isPayloadItemCollection(fallbackBucket)) {
        return fallbackBucket;
    }
    return 'saleItems';
};

const tagItemsWithPayloadCategory = (
    items: any[] = [],
    bucket: PayloadItemCollection,
) =>
    items.map((item) => ({
        ...item,
        [PAYLOAD_CATEGORY_FIELD]: resolvePayloadBucket(item, bucket),
    }));

/**
 * Re-groups line items into saleItems / addOns / mpwItems / etc. using
 * profitability_payload_category from the DB (or offer_order_line_items).
 */
const organizePayloadByCategory = (data: Record<string, any> = {}) => {
    const buckets: Record<PayloadItemCollection, any[]> = {
        saleItems: [],
        addOns: [],
        additionalItems: [],
        mpwItems: [],
        sisServices: [],
    };

    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        (data[key] || []).forEach((item: any) => {
            const bucket = resolvePayloadBucket(item, key);
            buckets[bucket].push({
                ...item,
                [PAYLOAD_CATEGORY_FIELD]: bucket,
            });
        });
    });

    return { ...data, ...buckets };
};

const buildPayloadItemIndex = (data: Record<string, any> = {}) => {
    const byItemId = new Map<string, Record<string, any>>();
    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        (data[key] || []).forEach((item: any) => {
            if (item?.itemId != null && item.itemId !== '') {
                byItemId.set(String(item.itemId), {
                    ...item,
                    [PAYLOAD_CATEGORY_FIELD]: resolvePayloadBucket(item, key),
                });
            }
        });
    });
    return byItemId;
};

const listPayloadLineItems = (data: Record<string, any> = {}) => {
    const items: Record<string, any>[] = [];
    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        (data[key] || []).forEach((item: any) => items.push(item));
    });
    return items;
};

const buildItemIdByProductCode = (data: Record<string, any> = {}) => {
    const map = new Map<string, string>();
    listPayloadLineItems(data).forEach((item) => {
        if (item?.productCode != null && item?.itemId != null) {
            map.set(String(item.productCode), String(item.itemId));
        }
    });
    return map;
};

const resolvePayloadItemId = (
    key: string,
    row: ProfitabilityBreakdownRow | undefined,
    payloadItems: Map<string, Record<string, any>>,
    itemIdByProductCode: Map<string, string>,
): string | undefined => {
    if (payloadItems.has(key)) {
        return key;
    }
    if (
        row?.itemId != null &&
        row.itemId !== '' &&
        payloadItems.has(String(row.itemId))
    ) {
        return String(row.itemId);
    }
    if (row?.productCode != null) {
        const fromCode = itemIdByProductCode.get(String(row.productCode));
        if (fromCode && payloadItems.has(fromCode)) {
            return fromCode;
        }
    }
    return undefined;
};

export const mergeModifiedItemMaps = (
    ...sources: Array<Record<string, Record<string, unknown>> | undefined>
): Record<string, Record<string, unknown>> => {
    const merged: Record<string, Record<string, unknown>> = {};
    sources.forEach((source) => {
        Object.entries(source ?? {}).forEach(([key, mods]) => {
            if (!mods || !Object.keys(mods).length) {
                return;
            }
            merged[key] = { ...(merged[key] ?? {}), ...mods };
        });
    });
    return merged;
};

/**
 * Maps grid edit keys (row.id / uniqueId) to payload itemId before Calculate.
 * Grid rows often use uniqueId as id while payload line items use itemId.
 */
export const resolveModifiedItemsForPayload = (
    modifiedItems: Record<string, Record<string, unknown>> = {},
    payloadData: Record<string, any> = {},
    gridRows: ProfitabilityBreakdownRow[] = [],
): Record<string, Record<string, unknown>> => {
    const payloadItems = buildPayloadItemIndex(payloadData);
    const itemIdByProductCode = buildItemIdByProductCode(payloadData);
    const lineItems = listPayloadLineItems(payloadData);
    const rowByGridId = new Map(
        gridRows.map((row) => [String(row.id), row]),
    );
    const resolved: Record<string, Record<string, unknown>> = {};

    Object.entries(modifiedItems).forEach(([key, mods]) => {
        if (!mods || !Object.keys(mods).length) {
            return;
        }
        const row = rowByGridId.get(key);
        let payloadItemId = resolvePayloadItemId(
            key,
            row,
            payloadItems,
            itemIdByProductCode,
        );
        if (!payloadItemId) {
            const gridIndex = gridRows.findIndex(
                (candidate) => String(candidate.id) === key,
            );
            if (gridIndex >= 0) {
                payloadItemId = resolvePayloadItemId(
                    key,
                    gridRows[gridIndex],
                    payloadItems,
                    itemIdByProductCode,
                );
                if (
                    !payloadItemId &&
                    lineItems[gridIndex]?.itemId != null &&
                    lineItems[gridIndex].itemId !== ''
                ) {
                    payloadItemId = String(lineItems[gridIndex].itemId);
                }
            }
        }
        if (!payloadItemId) {
            return;
        }
        resolved[payloadItemId] = {
            ...(resolved[payloadItemId] ?? {}),
            ...mods,
        };
    });

    return resolved;
};

export const enrichRowsWithPayloadCategories = (
    rows: ProfitabilityBreakdownRow[] = [],
    payloadData: Record<string, any> = {},
) => {
    const itemById = buildPayloadItemIndex(payloadData);
    const itemIdByProductCode = buildItemIdByProductCode(payloadData);
    const lineItems = listPayloadLineItems(payloadData);
    return rows.map((row, index) => {
        const resolvedItemId =
            row.itemId != null && row.itemId !== ''
                ? String(row.itemId)
                : row.productCode != null
                  ? itemIdByProductCode.get(String(row.productCode))
                  : lineItems[index]?.itemId != null
                    ? String(lineItems[index].itemId)
                    : row.id != null && itemById.has(String(row.id))
                      ? String(row.id)
                      : undefined;
        const item = resolvedItemId ? itemById.get(resolvedItemId) : undefined;
        if (!item?.[PAYLOAD_CATEGORY_FIELD]) {
            return resolvedItemId ? { ...row, itemId: resolvedItemId } : row;
        }
        return {
            ...row,
            itemId: resolvedItemId,
            [PAYLOAD_CATEGORY_FIELD]: item[PAYLOAD_CATEGORY_FIELD],
        };
    });
};

type CadOverrideFieldConfig = {
    modifyKeys: readonly string[];
    payloadKey: string;
    overrideKey: string;
};

const CAD_OVERRIDE_FIELDS: CadOverrideFieldConfig[] = [
    {
        modifyKeys: ['cadVal', 'cad'],
        payloadKey: 'cadVal',
        overrideKey: 'cadValOverride',
    },
    {
        modifyKeys: ['cadPercent'],
        payloadKey: 'cadPercent',
        overrideKey: 'cadPercentOverride',
    },
    {
        modifyKeys: ['unitCost'],
        payloadKey: 'unitCost',
        overrideKey: 'unitCostOverride',
    },
];

const toComparableNumber = (value: unknown): number | null => {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const valuesEqual = (left: unknown, right: unknown): boolean =>
    toComparableNumber(left) === toComparableNumber(right);

const resolvePayloadFieldValue = (
    item: Record<string, unknown>,
    payloadKey: string,
) => {
    if (payloadKey === 'cadVal') {
        return item.cadVal ?? item.cad;
    }
    return item[payloadKey];
};

const applyCadOverrideFields = (
    item: Record<string, unknown>,
    modifications: Record<string, unknown>,
) => {
    const merged = { ...item };
    const remainingMods = { ...modifications };
    const cadValModified =
        modifications.cadVal !== undefined || modifications.cad !== undefined;
    const cadPercentModified = modifications.cadPercent !== undefined;

    CAD_OVERRIDE_FIELDS.forEach(({ modifyKeys, payloadKey, overrideKey }) => {
        const modKey = modifyKeys.find((key) => remainingMods[key] !== undefined);
        if (!modKey) {
            return;
        }

        const newValue = remainingMods[modKey];
        modifyKeys.forEach((key) => {
            delete remainingMods[key];
        });

        merged[payloadKey] = resolvePayloadFieldValue(item, payloadKey);
        merged[overrideKey] = newValue;
        if (payloadKey === 'cadVal') {
            delete merged.cad;
        }
    });

    // Send only the CAD dimension the user edited (base + *Override); omit the other.
    if (cadPercentModified && !cadValModified) {
        delete merged.cadVal;
        delete merged.cad;
        delete merged.cadValOverride;
    }
    if (cadValModified && !cadPercentModified) {
        delete merged.cadPercent;
        delete merged.cadPercentOverride;
    }

    return { ...merged, ...remainingMods };
};

/**
 * Returns a new payload `data` object where only the modified fields of the
 * changed items (matched by itemId) are overridden — all other values are kept.
 * CAD / CAD % / unit cost edits keep the previous payload value on the base
 * field and send the edited grid value in the matching *Override field.
 */
const applyModifiedItems = (
    data: any,
    modifiedItems: Record<string, Record<string, unknown>> = {},
) => {
    const next = { ...data };
    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        if (Array.isArray(next[key])) {
            next[key] = next[key].map((item: any) => {
                if (item?.itemId == null || item.itemId === '') {
                    return item;
                }
                const itemMods = modifiedItems[String(item.itemId)];
                if (!itemMods) {
                    return item;
                }
                return applyCadOverrideFields(item, itemMods);
            });
        }
    });
    return organizePayloadByCategory(next);
};

const preserveCadOverrideFieldsOnPayload = (
    sourceData: Record<string, any>,
    targetData: Record<string, any>,
) => {
    const sourceItems = buildPayloadItemIndex(sourceData);
    const next = { ...targetData };

    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        if (!Array.isArray(next[key])) {
            return;
        }
        next[key] = next[key].map((item: any) => {
            if (item?.itemId == null) {
                return item;
            }
            const sourceItem = sourceItems.get(String(item.itemId));
            if (!sourceItem) {
                return item;
            }

            const preserved: Record<string, unknown> = {};
            CAD_OVERRIDE_FIELDS.forEach(({ payloadKey, overrideKey }) => {
                if (sourceItem[overrideKey] !== undefined) {
                    preserved[payloadKey] = sourceItem[payloadKey];
                    preserved[overrideKey] = sourceItem[overrideKey];
                }
            });

            return Object.keys(preserved).length
                ? { ...item, ...preserved }
                : item;
        });
    });

    return organizePayloadByCategory(next);
};

/** Grid-only fields that must not be copied onto payload line items. */
const ROW_TO_PAYLOAD_SKIP_KEYS = new Set(['id', 'uniqueId', 'orgHierarchy']);

const isNumericHeaderColumn = (col: ProfitabilityHeaderColumn): boolean =>
    col?.type === 'number' ||
    col?.key === 'unitCost' ||
    col?.isEditable === true ||
    col?.isEditable === 'true';

const numericKeysFromHeader = (header: ProfitabilityHeaderColumn[] = []): Set<string> => {
    const keys = new Set<string>(['unitCost']);
    header.forEach((col) => {
        if (col?.key && isNumericHeaderColumn(col)) {
            keys.add(col.key);
        }
    });
    return keys;
};

/** CAD fields are sent via base + *Override on the line item, not from grid rows. */
const ROW_OVERRIDE_MANAGED_FIELDS = new Set([
    'cad',
    'cadVal',
    'cadPercent',
    'unitCost',
]);

/**
 * Flattens nested l2l3/l3l2 rows into AG Grid tree rows (`orgHierarchy`).
 * Flat modes pass through with a single-level path.
 * Empty / non-numeric values on numeric columns (e.g. unitCost: "") become 0.
 */
const normaliseRows = (
    rows: any[] = [],
    options: { nested?: boolean; header?: ProfitabilityHeaderColumn[] } = {},
): ProfitabilityBreakdownRow[] => {
    const numericKeys = numericKeysFromHeader(options.header);
    const flatten = (
        nodes: any[],
        parentPath: string[] = [],
        usedIds: Set<string> = new Set(),
    ): ProfitabilityBreakdownRow[] => {
        const result: ProfitabilityBreakdownRow[] = [];

        nodes.forEach((row: any, index: number) => {
            const baseId = String(
                row?.uniqueId ??
                    row?.itemId ??
                    (parentPath.length
                        ? `${parentPath.join("-")}-${index}`
                        : index),
            );
            let id = baseId;
            if (usedIds.has(id)) {
                id = parentPath.length
                    ? `${parentPath.join("-")}-${baseId}-${index}`
                    : `${baseId}-${index}`;
            }
            usedIds.add(id);
            const path = options.nested ? [...parentPath, id] : [id];
            const { children, ...rest } = row || {};
            const next: Record<string, unknown> = {
                ...rest,
                id,
                orgHierarchy: path,
            };
            numericKeys.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(next, key)) {
                    next[key] = toNumericOrZero(next[key]);
                }
            });
            result.push(next as ProfitabilityBreakdownRow);

            if (options.nested && Array.isArray(children) && children.length) {
                result.push(...flatten(children, path, usedIds));
            }
        });

        return result;
    };

    return flatten(rows);
};

/**
 * After Calculate, merge calculated row values (e.g. cad, cadPercent) back into
 * the request item collections so Apply can send the calc response as-is.
 */
export const mergeCalcRowsIntoPayloadData = (
    data: any,
    rows: ProfitabilityBreakdownRow[] = [],
) => {
    const rowByItemId = new Map(
        rows
            .filter((row) => row.itemId != null && row.itemId !== '')
            .map((row) => [String(row.itemId), row]),
    );
    const next = { ...data };
    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        if (!Array.isArray(next[key])) return;
        next[key] = next[key].map((item: any) => {
            const row = item?.itemId != null
                ? rowByItemId.get(String(item.itemId))
                : undefined;
            if (!row) return item;
            const updates: Record<string, unknown> = {};
            Object.entries(row).forEach(([field, value]) => {
                if (
                    ROW_TO_PAYLOAD_SKIP_KEYS.has(field) ||
                    ROW_OVERRIDE_MANAGED_FIELDS.has(field)
                ) {
                    return;
                }
                if (
                    field in item ||
                    ['glp', 'duration', 'price', PAYLOAD_CATEGORY_FIELD].includes(field)
                ) {
                    updates[field] = value;
                }
            });
            return Object.keys(updates).length ? { ...item, ...updates } : item;
        });
    });
    return organizePayloadByCategory(next);
};

/**
 * Finds the offer being analysed inside the dashboard `caseResults` slice.
 * Used only as a soft fallback when the offer API returns nothing.
 */
const findDashboardOffer = (state: any, offerId?: string) => {
    const caseResults: any[] = state?.dashboard?.data?.caseResults || [];
    if (!offerId) return caseResults[0];
    return caseResults.find(
        (offer: any) => offer?.offer_id === offerId || offer?.temp_offer_id === offerId,
    );
};

const buildCaseInfo = (
    offer: ProfitabilityOffer | null | undefined,
    offerId: string | undefined,
    currency: string | undefined,
): ProfitabilityCaseInfo => ({
    caseId: offerId || offer?.offer_id || offer?.temp_offer_id || "",
    caseCode: offer?.sCRMID || offer?.caseCode || "",
    caseName: offer?.caseName || offer?.case_name || "",
    currency: currency || offer?.currency || "",
    currencySymbol: offer?.currencySymbol || "",
    totalCost: 0,
});

const resolveOfferId = (state: any, fallbackOfferId?: string): string => {
    const analysis = state?.profitability?.data?.analysis;
    return (
        fallbackOfferId ||
        analysis?.caseInfo?.caseId ||
        analysis?.offer?.offer_id ||
        analysis?.offer?.temp_offer_id ||
        analysis?.payload?.data?.offerId ||
        ""
    );
};

const headerToColumnDefs = (header: any[] = []) =>
    header
        .filter((col) => col?.displayOnUI !== false && col?.key)
        .map((col) => ({
            field: col.key,
            headerName: col.label || col.key,
            locked: col.isDisabled === true,
        }));

/** Merges item collections returned by datamart into the outbound request body. */
const mergeItemCollection = (
    key: PayloadItemCollection,
    baseData: Record<string, any>,
    serverPayload?: Record<string, any> | null,
) => {
    const serverItems = serverPayload?.[key];
    if (Array.isArray(serverItems) && serverItems.length > 0) {
        return serverItems;
    }
    return baseData[key];
};

const mergeServerPayload = (baseData: Record<string, any>, serverPayload?: Record<string, any> | null) => {
    if (!serverPayload) return organizePayloadByCategory(baseData);

    const merged: Record<string, any> = {
        ...baseData,
        ...serverPayload,
    };

    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        merged[key] = mergeItemCollection(key, baseData, serverPayload);
    });

    return organizePayloadByCategory(merged);
};

const pickItemCollections = (data: Record<string, any> = {}) => ({
    saleItems: tagItemsWithPayloadCategory(data.saleItems, 'saleItems'),
    addOns: tagItemsWithPayloadCategory(data.addOns, 'addOns'),
    additionalItems: tagItemsWithPayloadCategory(data.additionalItems, 'additionalItems'),
    mpwItems: tagItemsWithPayloadCategory(data.mpwItems, 'mpwItems'),
    sisServices: tagItemsWithPayloadCategory(data.sisServices, 'sisServices'),
});

type ProfitabilityUIResponse = Awaited<
    ReturnType<typeof DATAMART_SERVICES.fetchProfitabilityUI>
>;

/** Grid uses `cad`; API expects `cadVal` on each line item. */
const sanitizeLineItemForApi = (item: Record<string, any> = {}) => {
    const { cad, ...rest } = item;
    if (rest.cadVal == null && cad) {
        return { ...rest, cadVal: cad };
    }
    return rest;
};

const sanitizePayloadForApi = (data: Record<string, any>) => {
    const next = { ...data };
    PROFITABILITY_ITEM_COLLECTIONS.forEach((key) => {
        if (Array.isArray(next[key])) {
            next[key] = next[key].map(sanitizeLineItemForApi);
        }
    });
    return next;
};

/** POST /v2/pricing/profitability — backend enriches payload on calc/apply. */
const invokeProfitabilityUI = (
    action: string,
    data: Record<string, any>,
) =>
    DATAMART_SERVICES.fetchProfitabilityUI({
        action,
        data: sanitizePayloadForApi({ ...data, action }),
    });

/** Strips stale action before sending a fresh engine request. */
const prepareCalcRequestData = (
    baseData: Record<string, any>,
    modifiedItems?: Record<string, Record<string, unknown>>,
) => {
    const { action: _staleAction, ...requestFields } = baseData;
    return organizePayloadByCategory(
        applyModifiedItems(requestFields, modifiedItems),
    );
};

const buildAnalysisReturn = (
    resp: ProfitabilityUIResponse,
    offer: ProfitabilityOffer | null,
    resolvedPostData: { data: Record<string, any> },
    prefsResult: Awaited<ReturnType<typeof DATAMART_SERVICES.getTablePreferences>>,
    offerId: string,
) => {
    const header = resp.header || [];
    const columnDefs = headerToColumnDefs(header);
    const resolvedPrefs = resolveAnalysisColumnPrefs(prefsResult, columnDefs);
    const columnPrefs = resolvedPrefs.columns.length
        ? normalizePrefsVisibility(resolvedPrefs.columns)
        : toColumnPrefs(columnDefs, { defaultVisible: false });

    return {
        offer,
        caseInfo: buildCaseInfo(offer, offerId, resolvedPostData.data.currency),
        header,
        rows: enrichRowsWithPayloadCategories(
            normaliseRows(resp.rows, { header }),
            resolvedPostData.data,
        ),
        payload: resolvedPostData,
        itemSource: resp.itemSource,
        columnPrefs,
        exportChecked: resolvedPrefs.exportChecked,
        ucpId: resolvedPrefs.ucpId,
        prefsLoaded: true,
        viewMode: "view" as ProfitabilityViewMode,
        subscriptionDurationMonths: toDurationMonths(
            resp.metadata?.subscriptionDurationMonths,
        ),
    };
};

/** Offer subscription duration from load metadata; 0 when absent or not a valid number. */
const toDurationMonths = (value: unknown): number => {
    if (value === undefined || value === null || value === '') return 0;
    const months = Number(value);
    return Number.isFinite(months) ? months : 0;
};

/**
 * Default empty item collections for /v2/pricing/profitability.
 */
const EMPTY_PROFITABILITY_ITEMS = {
    saleItems: [] as unknown[],
    addOns: [] as unknown[],
    additionalItems: [] as unknown[],
    mpwItems: [] as unknown[],
    sisServices: [] as unknown[],
};

const resolveProfitabilitySolutionName = (offer: ProfitabilityOffer = {}) =>
    offer.solution ||
    offer.solution_name ||
    offer.lead_product ||
    '';

const resolveProfitabilityAccountOneStarNo = (offer: ProfitabilityOffer = {}) =>
    offer.sell_to_party_accountnumber ||
    offer.account_number ||
    offer.account_one_star_number ||
    '';

/** Builds profitability request from offer metadata; line items are resolved by the backend on load. */
const buildProfitabilityPayload = (
    offer: ProfitabilityOffer,
    userEmail?: string,
    itemCollections: Record<string, unknown[]> = EMPTY_PROFITABILITY_ITEMS,
    action = 'calc',
): Record<string, any> => ({
    accountOneStarNo: resolveProfitabilityAccountOneStarNo(offer),
    accountThreeStarNo: offer.sell_to_party_three_star_accountnumber,
    solutionName: resolveProfitabilitySolutionName(offer),
    solutionType: (offer.solutionCategory || '').toLowerCase(),
    priceListName: offer.priceListName || offer.price_list_name || '',
    currency: offer.currency,
    opportunityId: offer.sCRMID,
    country: offer.shipping_country,
    source: offer.source || 'cui',
    offerId: offer.offer_id || offer.temp_offer_id,
    userEmail: userEmail || '',
    commercialModel: offer.commercial_model,
    commercialModel_Offer: offer.commercial_model,
    profitability_wrap: true,
    action,
    ...pickItemCollections(itemCollections),
});

/**
 * Analyse page load: fetch offer metadata → POST action "load".
 *
 * Datamart resolves line items from the profitability table or offer_order_line_items
 * and returns the same response shape in both cases.
 */
export const fetchProfitabilityAnalysis = createAsyncThunk(
    'profitability/fetchProfitabilityAnalysis',
    async (offerId: string | undefined, thunkAPI) => {
        try {
            if (!offerId) {
                return thunkAPI.rejectWithValue('Offer id is required');
            }

            const state: any = thunkAPI.getState();
            const userEmail = state?.authSlice?.login?.mail;
            const dashboardOffer = findDashboardOffer(state, offerId);

            const [prefsResult, offerResult] = await Promise.all([
                DATAMART_SERVICES.getTablePreferences(
                    PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
                ),
                DATAMART_SERVICES.fetchOfferForAnalysis(offerId),
            ]);

            const offer =
                (offerResult?.status && offerResult.data) ||
                dashboardOffer ||
                null;

            if (!offer) {
                return thunkAPI.rejectWithValue({
                    message: offerResult?.message || 'Offer not found',
                    retryable: false,
                });
            }

            const blockReason = getAnalyseProfitabilityBlockReason(offer);
            if (blockReason) {
                return thunkAPI.rejectWithValue({
                    message: blockReason,
                    retryable: false,
                });
            }

            const requestData = buildProfitabilityPayload(
                offer,
                userEmail,
                EMPTY_PROFITABILITY_ITEMS,
                'load',
            );

            const resp = await invokeProfitabilityUI('load', requestData);
            if (!resp.status) {
                return thunkAPI.rejectWithValue(
                    resp.message || 'Failed to fetch profitability analysis',
                );
            }

            const resolvedPostData = {
                data: mergeServerPayload(requestData, resp.payload),
            };
            return buildAnalysisReturn(
                resp,
                offer,
                resolvedPostData,
                prefsResult,
                offerId,
            );
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    },
    {
        condition: (offerId, { getState }) => {
            if (!offerId) return false;
            const analysis = (getState() as any)?.profitability?.data?.analysis;
            return !analysis?.loading;
        },
    },
);

/** Loads cockpit reserves for the Analyse page (GET /v2/pricing/getCockpit). */
export const fetchCockpit = createAsyncThunk(
    'profitability/fetchCockpit',
    async (offerId: string | undefined, thunkAPI) => {
        try {
            if (!offerId) {
                return thunkAPI.rejectWithValue('Offer id is required');
            }

            const resp = await DATAMART_SERVICES.fetchCockpit(offerId);
            if (!resp.status) {
                return thunkAPI.rejectWithValue(
                    resp.message || 'Failed to fetch cockpit data',
                );
            }

            return parseCockpitPayload(resp.data);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    },
);

/** Loads cockpit config rows for the create-new flow (GET /v2/pricing/cockpit/getCockpitConfig). */
export const cockpitGetConfigs = createAsyncThunk(
    'profitability/cockpitGetConfigs',
    async (offerId: string | undefined, thunkAPI) => {
        try {
            if (!offerId) {
                return thunkAPI.rejectWithValue('Offer id is required');
            }

            const resp = await DATAMART_SERVICES.cockpitGetConfigs(offerId);
            if (!resp.status) {
                return thunkAPI.rejectWithValue(
                    resp.message || 'Failed to fetch cockpit config',
                );
            }

            return parseCockpitPayload(resp.data);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    },
);

/**
 * Switches Profitability breakdown level view.
 * - `view` restores the last View snapshot (no re-POST) when available
 * - L2 / L3 / L2+L3 / L3+L2 call GET endpoints with offerId only
 */
export const fetchProfitabilityByMode = createAsyncThunk(
    'profitability/fetchProfitabilityByMode',
    async (
        params: { mode: ProfitabilityViewMode; offerId?: string },
        thunkAPI,
    ) => {
        try {
            const { mode } = params;
            const state: any = thunkAPI.getState();
            const analysis = state?.profitability?.data?.analysis;

            if (mode === 'view') {
                const snapshot = analysis?.viewSnapshot;
                if (snapshot?.header?.length) {
                    return {
                        mode,
                        header: snapshot.header,
                        rows: snapshot.rows || [],
                        fromSnapshot: true,
                    };
                }

                const basePayload = analysis?.payload;
                if (!basePayload?.data) {
                    return thunkAPI.rejectWithValue(
                        'No profitability View data available. Load analysis first.',
                    );
                }

                const resp = await DATAMART_SERVICES.fetchProfitabilityUI(basePayload);
                if (!resp.status) {
                    return thunkAPI.rejectWithValue(
                        resp.message || 'Failed to fetch profitability View',
                    );
                }

                return {
                    mode,
                    header: resp.header || [],
                    rows: normaliseRows(resp.rows, { header: resp.header || [] }),
                    fromSnapshot: false,
                };
            }

            const offerId = resolveOfferId(state, params.offerId);
            if (!offerId) {
                return thunkAPI.rejectWithValue('Offer id is required');
            }

            const pathSegment = PROFITABILITY_MODE_PATH[mode];
            const resp = await DATAMART_SERVICES.fetchProfitabilityByMode(
                pathSegment,
                offerId,
            );
            if (!resp.status) {
                return thunkAPI.rejectWithValue(
                    resp.message || `Failed to fetch profitability ${mode}`,
                );
            }

            return {
                mode,
                header: resp.header || [],
                rows: normaliseRows(resp.rows, {
                    nested: isNestedProfitabilityMode(mode),
                    header: resp.header || [],
                }),
                fromSnapshot: false,
            };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    },
);

/**
 * Recalculates profitability by re-calling /v2/pricing/profitability with the payload
 * stored in redux, overriding only the modified item values.
 * Calculate -> action "calc", Apply changes -> action "Apply".
 */
export const calculateProfitability = createAsyncThunk(
    'profitability/calculateProfitability',
    async (
        params: {
            action: string;
            modifiedItems?: Record<string, Record<string, unknown>>;
            gridRows?: ProfitabilityBreakdownRow[];
            useApplyPayload?: boolean;
        },
        thunkAPI,
    ) => {
        try {
            const state: any = thunkAPI.getState();
            const analysis = state?.profitability?.data?.analysis;
            const isApply = params.action === 'Apply' || params.action === 'apply';

            let requestData: Record<string, any>;
            if (isApply) {
                const storedApplyPayload = analysis?.applyPayload;
                if (!storedApplyPayload?.data) {
                    return thunkAPI.rejectWithValue(
                        'No calculated payload available. Run Calculate first.',
                    );
                }
                requestData = organizePayloadByCategory(storedApplyPayload.data);
            } else {
                const basePayload = analysis?.payload;
                if (!basePayload?.data) {
                    return thunkAPI.rejectWithValue(
                        'No profitability payload available. Load analysis first.',
                    );
                }
                const rawModifiedItems = mergeModifiedItemMaps(
                    analysis?.pendingModifiedItems,
                    params.modifiedItems,
                );
                const enrichedGridRows = enrichRowsWithPayloadCategories(
                    params.gridRows ?? analysis?.rows ?? [],
                    basePayload.data,
                );
                const resolvedModifiedItems = resolveModifiedItemsForPayload(
                    rawModifiedItems,
                    basePayload.data,
                    enrichedGridRows,
                );
                requestData = prepareCalcRequestData(
                    {
                        ...basePayload.data,
                        ...organizePayloadByCategory(pickItemCollections(basePayload.data)),
                    },
                    resolvedModifiedItems,
                );
            }

            const sentData = { ...requestData, action: params.action };
            const resp = await invokeProfitabilityUI(params.action, requestData);
            if (!resp.status) {
                return thunkAPI.rejectWithValue(resp.message || 'Failed to calculate profitability');
            }

            const isCalc = params.action === 'calc';
            const resolvedData = preserveCadOverrideFieldsOnPayload(
                sentData,
                mergeServerPayload(sentData, resp.payload),
            );
            const rows = enrichRowsWithPayloadCategories(
                normaliseRows(resp.rows, { header: resp.header || [] }),
                resolvedData,
            );
            const resolvedPostData = { data: resolvedData };
            const applyPayload = isCalc
                ? {
                      data: preserveCadOverrideFieldsOnPayload(
                          sentData,
                          mergeCalcRowsIntoPayloadData(resolvedData, rows),
                      ),
                  }
                : undefined;

            return {
                header: resp.header || [],
                rows,
                payload: resolvedPostData,
                applyPayload,
            };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    },
);

/** Applies cockpit grid changes via POST /v2/pricing/applyCockpit. */
export const applyCockpit = createAsyncThunk(
    'profitability/applyCockpit',
    async (
        params: {
            offerId: string;
            rows: CockpitRow[];
            columns: CockpitColumnDef[];
        },
        thunkAPI,
    ) => {
        try {
            const state: any = thunkAPI.getState();
            const analysis = state?.profitability?.data?.analysis;
            const basePayload = analysis?.payload?.data;
            if (!basePayload) {
                return thunkAPI.rejectWithValue(
                    'No profitability payload available. Load analysis first.',
                );
            }

            const discount_cockpit = buildDiscountCockpitPayload(
                params.rows,
                params.columns,
            );

            const requestData = organizePayloadByCategory({
                ...basePayload,
                ...organizePayloadByCategory(pickItemCollections(basePayload)),
                action: 'Apply',
            });

            const resp = await DATAMART_SERVICES.applyCockpit({
                action: 'Apply',
                data: requestData,
                discount_cockpit,
            });
            if (!resp.status) {
                return thunkAPI.rejectWithValue(
                    resp.message || 'Failed to apply cockpit changes',
                );
            }

            const resolvedData = mergeServerPayload(requestData, resp.payload);
            const header =
                resp.header?.length > 0
                    ? resp.header
                    : analysis?.header?.length > 0
                      ? analysis.header
                      : [];
            const profitabilityRows = enrichRowsWithPayloadCategories(
                normaliseRows(resp.rows, { header: resp.header || [] }),
                resolvedData,
            );
            const columnDefs = headerToColumnDefs(header);
            const existingPrefs = analysis?.columnPrefs ?? [];
            const prefsLoaded = !!analysis?.prefsLoaded;
            const columnPrefs = columnDefs.length
                ? normalizePrefsVisibility(
                      prefsLoaded && existingPrefs.length
                          ? mergeApiColumnsWithDefs(existingPrefs, columnDefs)
                          : toColumnPrefs(columnDefs, { defaultVisible: false }),
                  )
                : undefined;

            return {
                header,
                rows: profitabilityRows,
                payload: { data: resolvedData },
                columnPrefs,
                cockpitRows: normaliseCockpitRowData(
                    cloneCockpitRows(params.rows),
                    params.columns,
                ),
            };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    },
);


// Define an async thunk for fetching Profitbility Summary
export const fetchProfitbilitySummary = createAsyncThunk(
    'profitability/fetchProfitbilitySummary',
    async (param, thunkAPI) => {
        try {
            // Mock the summary response
            // Replace the below mock response with API response
            const data = await new Promise((resolve) =>
                setTimeout(() => {
                    const mockResponse = [
                        { title: "Total deals", value: Math.floor(Math.random() * 100) + 50, color: "purple" },
                        { title: "Deal won", value: Math.floor(Math.random() * 150) + 10, color: "green" },
                        { title: "Deal lost", value: Math.floor(Math.random() * 130) + 5, color: "red" },
                        { title: "In progress", value: Math.floor(Math.random() * 120) + 5, color: "blue" },
                        { title: "Pending approval", value: Math.floor(Math.random() * 10) + 1, color: "orange" },
                    ];
                    resolve(mockResponse)
                }, 1000)
            );
            return { status: true, data };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    }
);

// Define an async thunk for fetching Price List
export const fetchPriceList = createAsyncThunk(
    'profitability/fetchPriceList',
    async (params: PriceListParams, thunkAPI) => {
        const { currentPage, itemsPerPage, sortOption, searchKeyword } = params;
        const apiParam = {
            limit: itemsPerPage,
            offset: (currentPage - 1) * itemsPerPage,
            sort: sortOption,
            search: searchKeyword,
        };
        try {
            const resp = await DATAMART_SERVICES.fetchPriceList(apiParam);
            if (!resp.status) {
                return thunkAPI.rejectWithValue((resp as any)?.["message"] || 'Failed to fetch price list');
            }
            // Get all account numbers from the response
            // const filteredData = resp.data.filter((item: any) => item.account_number !== null);
            // const accountNumbers = filteredData.map((item: any) => item.account_number);
            // Flatten all account numbers (comma separated) into a single array
            const accountNumbers = resp.data
                .flatMap((item: any) =>
                    typeof item.account_number === "string"
                        ? item.account_number.split(",").map((num: string) => num.trim())
                        : [item.account_number]
                )
                .filter((num: any) => num); // Remove empty/null values
            // Prepare the account numbers for the API request
            // Remove the duplicates from accountNumbers
            let uniqueAccountNumbers: string[] = Array.from(new Set(accountNumbers));
            let accountMapList: any[] = [];
            if (uniqueAccountNumbers.length > 30) {
                const chunkSize = 30;
                const accountNumberChunks: string[][] = [];
                for (let i = 0; i < uniqueAccountNumbers.length; i += chunkSize) {
                    accountNumberChunks.push(uniqueAccountNumbers.slice(i, i + chunkSize));
                }
                const filters = accountNumberChunks.map(chunk => [
                    'accountnumber in (' + chunk.map(accountNumber => `"${accountNumber}"`).join(",") + ')'
                ]);
                /**
                 * Call the API for each filter chunk and combine the results
                 * eg: filters: [['accountnumber in ("A1","A2",...,"A30")'], ['accountnumber in ("A31","A32",...)'], ...]
                 */
                const results = await Promise.all(
                    filters.map(filter => DATAMART_SERVICES.getAccountList(filter as any))
                );
                accountMapList = results.flat();
            } else {
                const accountFilter = uniqueAccountNumbers && uniqueAccountNumbers?.length > 0 ? ['accountnumber in (' + uniqueAccountNumbers?.map(accountNumber => `"${accountNumber}"`).join(",") + ')'] : [];
                accountMapList = await DATAMART_SERVICES.getAccountList(accountFilter as any);
            }
            // Set Account Data - convert to desired format
            const accountData = accountMapList.map((item: any) => ({
                id: item.accountnumber,
                value: item.accountnumber + ` (${item.name})`,
            }));
            (resp as any)["accountMapList"] = accountData;
            return resp;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message || 'Unknown error');
        }
    }
);

// Define an async thunk for fetching Price List
export const deletePriceList = createAsyncThunk(
    'profitability/deletePriceList',
    async (priceListId: number, { dispatch, rejectWithValue }) => {
        if (!priceListId) {
            // dispatch an error message if priceListId is not provided
            PopupUtils.showError(dispatch, "Price list id is required");
        }
        try {
            const resp = await DATAMART_SERVICES.deletePriceList(priceListId);
            if (!resp.status) {
                // dispatch an error message
                PopupUtils.showError(dispatch, "Failed to delete price list");
                return rejectWithValue(resp?.["message"] || 'Failed to fetch price list');
            }
            // dispatch a success message
            PopupUtils.showSuccess(dispatch, resp?.message ? resp.message : "Price list deleted successfully");
            return resp;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Unknown error');
        }
    }
);

// Define an async thunk for fetching Solution Data
export const fetchSolutionData = createAsyncThunk(
    'profitability/fetchSolutionData',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const resp = await CONFIGURATOR_SERVICES.getSegmentList();
            if (!resp.status) {
                return rejectWithValue(resp?.["message"] || 'Failed to fetch segment list');
            }
            // If the response is successful, you can process the data as needed
            // Prepare the solution name array from the response data
            // Group the solutions by solution category
            const groupedSolutions = resp.data.reduce((acc: any, item: any) => {
                const key = item?.solutionCategory?.toLowerCase() || 'other';
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push({ id: item.segment, value: item.solution });
                return acc;
            }, {});
            return groupedSolutions;


        } catch (error: any) {
            return rejectWithValue(error.message || 'Unknown error');
        }
    }
);

// Define an async thunk for fetching Discount Data
export const fetchDiscountData = createAsyncThunk(
    'profitability/fetchDiscountData',
    async (params: any, { dispatch, rejectWithValue }) => {
        const { solutionType, priceListId } = params;
        // return values
        let solutionNames: { key: string, value: string }[] = [];
        try {
            const resp = await DATAMART_SERVICES.fetchPriceList({ solutionType, priceListId });
            return resp?.data || {};
        } catch (error: any) {
            return rejectWithValue(error.message || 'Unknown error');
        }
    }
);

// Define an async thunk for saving Discount Data
export const saveDiscountData = createAsyncThunk(
    'profitability/saveDiscountData',
    async (pricingData: any, { dispatch, rejectWithValue }) => {
        try {
            const resp = await DATAMART_SERVICES.saveDiscountData(pricingData);
            if (!resp.status) {
                // dispatch an error message
                PopupUtils.showError(dispatch, "Failed to save the price list data");
                return rejectWithValue(resp?.["message"] || 'Failed to fetch price list');
            }
            // dispatch a success message
            PopupUtils.showSuccess(dispatch, "Price list saved successfully");
            return resp;
        } catch (error: any) {
            // dispatch an error message
            PopupUtils.showError(dispatch, error.message || "Price list could not save");
            return rejectWithValue(error.message || 'Unknown error');
        }
    }
);

// Define an async thunk for fetching Account Data
export const fetchAccountData = createAsyncThunk(
    'profitability/fetchAccountData',
    async (params: any, { dispatch, rejectWithValue }) => {
        const { actionType, searchKey } = params;
        if (actionType === 'search' && searchKey !== '') {
            // Prepare the account filter for account search
            const accountFilter = [`accountnumber like '%${searchKey}%' OR name like '%${searchKey}%'`];
            // Call the api for maximum 100 result using a loop, each call will fetch limit 30 (backend restriction)
            let offset = 0;
            let hasMoreResults = true;
            const accountMapList = [];

            while (hasMoreResults && offset <= 100) {
                const result = await DATAMART_SERVICES.getAccountList(accountFilter as any, offset);
                accountMapList.push(...result);
                // offset += 30;
                offset += 10;
                hasMoreResults = result.length >= 10;
            }
            // Update the account list with the fetched data
            return { status: true, data: accountMapList };
        }
        else {
            // If not searching, fetch all accounts
            const resp = await DATAMART_SERVICES.getAccountList();
            return { status: true, data: resp };
        }
    }
);

export const fetchExportExcel = createAsyncThunk(
    "profitability/fetchExportExcel",
    async (params: fetchPriceListPayload, thunkAPI) => {
        try {
            const resp = await DATAMART_SERVICES.fetchPriceList(params);
            if (!resp.status) {
                return thunkAPI.rejectWithValue((resp as any)?.["message"] || 'Failed to fetch price list');
            }
            return resp;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);